/* PDP V1 enhancement only. It observes existing variant inputs and never changes color/gallery logic. */
(() => {
  const normalize = value => String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');

  function setupSizePreview(root) {
    if (root.dataset.pdpSizePreviewReady === 'true') return;
    root.dataset.pdpSizePreviewReady = 'true';
    const sizeName = normalize(root.dataset.sizeOption || 'size');
    const field = [...root.querySelectorAll('[data-option-name]')].find(item => normalize(item.dataset.optionName) === sizeName || normalize(item.dataset.optionName) === 'size');
    if (!field) return;

    const customValue = normalize(root.dataset.customSize || 'Custom Size');
    const customLabel = root.dataset.customSizeLabel;
    if (customValue && customLabel) {
      field.querySelectorAll('[data-option-id]').forEach(input => {
        if (normalize(input.value) === customValue) {
          const text = input.closest('.option-label')?.querySelector('span');
          if (text) text.textContent = customLabel;
        }
      });
    }

    const preview = root.querySelector('[data-size-preview]');
    if (!preview || !preview.dataset.sizeGuideSource) return;
    const measurements = new Map();
    let unit = 'in';
    const title = preview.querySelector('[data-size-preview-title]');
    const bust = preview.querySelector('[data-size-bust]');
    const waist = preview.querySelector('[data-size-waist]');
    const hips = preview.querySelector('[data-size-hips]');
    const unitGroup = preview.querySelector('[data-size-preview-units]');
    const unitButtons = [...preview.querySelectorAll('[data-pdp-size-unit]')];
    const format = value => `${value} ${unit}`.trim();
    const show = value => {
      const row = measurements.get(normalize(value));
      if (!row) { preview.hidden = true; return; }
      title.textContent = `${String(value).trim()} Measurements`;
      bust.textContent = format(row[unit].bust);
      waist.textContent = format(row[unit].waist);
      hips.textContent = format(row[unit].hips);
      preview.hidden = false;
    };
    const restore = () => show(field.querySelector('[data-option-id]:checked')?.value);

    field.addEventListener('change', event => {
      if (event.target.matches('[data-option-id]')) show(event.target.value);
    });
    field.addEventListener('pointerover', event => {
      const input = event.target.closest('.option-label')?.querySelector('[data-option-id]');
      if (input && matchMedia('(hover:hover)').matches) show(input.value);
    });
    field.addEventListener('pointerleave', restore);
    unitButtons.forEach(button => button.addEventListener('click', () => {
      unit = button.dataset.pdpSizeUnit;
      unitButtons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      restore();
    }));
    const status = root.querySelector('[data-pdp-status]');
    if (status) new MutationObserver(restore).observe(status, { childList: true, characterData: true, subtree: true });
    preview.setAttribute('aria-busy', 'true');
    const source = new URL(preview.dataset.sizeGuideSource, location.origin);
    source.searchParams.set('section_id', 'size-guide');
    const current = new URL(location.href);
    for (const name of ['preview_theme_id', '_fd', 'pb', 'country', 'currency']) {
      if (current.searchParams.has(name)) source.searchParams.set(name, current.searchParams.get(name));
    }
    fetch(source, { credentials: 'same-origin' })
      .then(response => {
        if (!response.ok) throw Error('Size Guide could not be loaded');
        return response.text();
      })
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('#size-chart tbody tr').forEach(row => {
          const label = row.querySelector('th[scope="row"]')?.textContent.trim();
          const cells = [...row.querySelectorAll('td[data-in][data-cm]')];
          if (!label || cells.length < 3) return;
          measurements.set(normalize(label), {
            in: { bust: cells[0].dataset.in, waist: cells[1].dataset.in, hips: cells[2].dataset.in },
            cm: { bust: cells[0].dataset.cm, waist: cells[1].dataset.cm, hips: cells[2].dataset.cm }
          });
        });
        if (measurements.size > 0 && unitGroup) unitGroup.hidden = false;
        restore();
      })
      .catch(() => { preview.hidden = true; })
      .finally(() => preview.removeAttribute('aria-busy'));
  }

  const usHolidayCache = new Map();
  function dateKey(date) {
    return [date.getFullYear(), date.getMonth() + 1, date.getDate()].join('-');
  }
  function usObservedHolidays(year) {
    if (usHolidayCache.has(year)) return usHolidayCache.get(year);
    const holidays = new Set();
    const fixed = (month, day) => {
      const date = new Date(year, month, day);
      if (date.getDay() === 6) date.setDate(date.getDate() - 1);
      else if (date.getDay() === 0) date.setDate(date.getDate() + 1);
      holidays.add(dateKey(date));
    };
    const nthWeekday = (month, weekday, occurrence) => {
      const first = new Date(year, month, 1);
      holidays.add(dateKey(new Date(year, month, 1 + (weekday - first.getDay() + 7) % 7 + 7 * (occurrence - 1))));
    };
    fixed(0, 1);
    nthWeekday(0, 1, 3);
    nthWeekday(1, 1, 3);
    const memorial = new Date(year, 5, 0);
    memorial.setDate(memorial.getDate() - (memorial.getDay() + 6) % 7);
    holidays.add(dateKey(memorial));
    fixed(5, 19);
    fixed(6, 4);
    nthWeekday(8, 1, 1);
    nthWeekday(10, 4, 4);
    fixed(11, 25);
    usHolidayCache.set(year, holidays);
    return holidays;
  }
  function isUSHoliday(date) {
    const year = date.getFullYear();
    const key = dateKey(date);
    return [year - 1, year, year + 1].some(value => usObservedHolidays(value).has(key));
  }
  function isUSShippingDay(date) {
    return date.getDay() !== 0 && date.getDay() !== 6 && !isUSHoliday(date);
  }

  function addDays(start, count, businessDays, excludeHolidays = true) {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    let remaining = Math.max(0, count);
    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      if (businessDays ? isUSShippingDay(date) : (!excludeHolidays || !isUSHoliday(date))) remaining -= 1;
    }
    return date;
  }

  function setupDelivery(scope = document) {
    scope.querySelectorAll('[data-estimated-delivery]').forEach(node => {
      if (node.dataset.ready === 'true') return;
      node.dataset.ready = 'true';
      const values = ['processingMin', 'processingMax', 'shippingMin', 'shippingMax', 'buffer'].map(key => Number(node.dataset[key]));
      if (!values.every(Number.isFinite) || values.slice(0, 4).some(value => value <= 0)) return;
      const [processingMin, processingMax, shippingMin, shippingMax, buffer] = values;
      if (processingMax < processingMin || shippingMax < shippingMin) return;
      const business = node.dataset.dayMode === 'business';
      // Use the same Pacific-time order cutoff as both Merchant Center services.
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: node.dataset.originTimezone || 'America/Los_Angeles',
        year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', hourCycle: 'h23'
      }).formatToParts(new Date());
      const part = type => Number(parts.find(value => value.type === type).value);
      const today = new Date(part('year'), part('month') - 1, part('day'));
      const cutoffHour = Number(node.dataset.orderCutoffHour || 14);
      const processingStart = part('hour') >= cutoffHour ? addDays(today, 1, false, false) : today;
      while (isUSHoliday(processingStart)) processingStart.setDate(processingStart.getDate() + 1);
      const earliest = addDays(addDays(processingStart, processingMin, false), shippingMin + buffer, business);
      const latest = addDays(addDays(processingStart, processingMax, false), shippingMax + buffer, business);
      const sameYear = earliest.getFullYear() === latest.getFullYear() && earliest.getFullYear() === today.getFullYear();
      const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
      const output = node.querySelector('[data-estimated-delivery-value]');
      if (!output) return;
      output.textContent = `${formatter.format(earliest)} – ${formatter.format(latest)}`;
      node.dataset.latestArrival = dateKey(latest);
      node.hidden = false;
    });
  }

  function setupReviews(section) {
    if (section.dataset.ready === 'true') return;
    section.dataset.ready = 'true';
    const list = section.querySelector('[data-review-list]');
    const sort = section.querySelector('[data-review-sort]');
    const more = section.querySelector('[data-review-more]');
    if (!list) return;
    const initial = Math.max(1, Number(section.dataset.initialCount) || 3);
    let expanded = false;
    const arrange = () => {
      const cards = [...list.querySelectorAll('[data-review-card]')];
      cards.sort((a, b) => sort?.value === 'highest'
        ? Number(b.dataset.rating) - Number(a.dataset.rating) || String(b.dataset.date).localeCompare(String(a.dataset.date))
        : String(b.dataset.date).localeCompare(String(a.dataset.date)));
      cards.forEach((card, index) => { list.append(card); card.hidden = !expanded && index >= initial; });
      if (more) more.hidden = expanded || cards.length <= initial;
    };
    sort?.addEventListener('change', arrange);
    more?.addEventListener('click', () => { expanded = true; arrange(); });
    arrange();
  }

  async function setupRecommendations(loader) {
    if (loader.dataset.ready === 'true' || !loader.dataset.url) return;
    loader.dataset.ready = 'true';
    try {
      const response = await fetch(loader.dataset.url, { credentials: 'same-origin' });
      if (!response.ok) throw Error('Recommendation request failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = doc.querySelector('[data-pdp-related-content]');
      if (content) loader.replaceWith(content);
      else loader.remove();
    } catch (error) {
      loader.remove();
    }
  }


  function setupPurchaseClarity(root) {
    if (root.dataset.purchaseClarityReady) return;
    root.dataset.purchaseClarityReady = 'true';
    const sizeField = [...root.querySelectorAll('[data-option-name]')].find(field => normalize(field.dataset.optionName) === normalize(root.dataset.sizeOption || 'Size'));
    const sync = () => {
      const custom = normalize(sizeField?.querySelector('input:checked')?.value) === normalize(root.dataset.customSize || 'Custom Size');
      root.querySelectorAll('[data-standard-return]').forEach(node => { node.hidden = custom; });
      root.querySelectorAll('[data-custom-return]').forEach(node => { node.hidden = !custom; });
      root.querySelectorAll('.color-options .option-label').forEach(label => { label.title = label.querySelector('input')?.value || ''; });
    };
    root.addEventListener('change', sync);
    sync();
    const planner = root.querySelector('[data-event-planner]');
    const dateInput = planner?.querySelector('[data-event-date]');
    dateInput?.addEventListener('change', () => {
      const result = planner.querySelector('[data-event-result]');
      const delivery = root.querySelector('[data-estimated-delivery]');
      const parse = value => { const parts = value.split('-').map(Number); return new Date(parts[0], parts[1] - 1, parts[2]); };
      if (!dateInput.value || !delivery?.dataset.latestArrival) { result.textContent = 'Compare your date with the estimated arrival. Allow time for fittings or alterations.'; return; }
      const event = parse(dateInput.value);
      const latest = parse(delivery.dataset.latestArrival);
      const today = new Date(); today.setHours(0,0,0,0);
      if (event <= today) result.textContent = 'Please choose a future event date.';
      else if (event <= latest) result.textContent = 'Your event falls before or within the estimated arrival window. Contact us before ordering to discuss timing; arrival is not guaranteed.';
      else {
        const days = Math.round((Date.UTC(event.getFullYear(),event.getMonth(),event.getDate()) - Date.UTC(latest.getFullYear(),latest.getMonth(),latest.getDate())) / 86400000);
        result.textContent = 'Your event is ' + days + ' days after the latest estimated arrival. Allow additional time for fittings and alterations. Delivery dates are estimates, not guarantees.';
      }
    });
  }


  function setupReviewScore(root) {
    root.querySelectorAll('.jdgm-preview-badge').forEach(widget => {
      if (widget.dataset.pdpScoreReady) return;
      widget.dataset.pdpScoreReady = 'true';
      const sync = () => {
        const badge = widget.querySelector('.jdgm-prev-badge');
        if (!badge) return;
        const average = Number(badge.dataset.averageRating);
        const count = Number(badge.dataset.numberOfReviews);
        let score = badge.querySelector('.pdp-review-average');
        if (!(count > 0 && average > 0 && average <= 5)) { score?.remove(); return; }
        const text = badge.querySelector('.jdgm-prev-badge__text');
        const stars = badge.querySelector('.jdgm-prev-badge__stars');
        if (!text || !stars) return;
        if (!score) {
          score = document.createElement('span');
          score.className = 'pdp-review-average';
        }
        const value = average.toFixed(1);
        if (score.textContent !== value) score.textContent = value;
        const label = value + ' out of 5 stars';
        if (score.getAttribute('aria-label') !== label) score.setAttribute('aria-label', label);
        if (text.parentElement !== badge || text.nextElementSibling !== stars) badge.insertBefore(text, stars);
        if (score.parentElement !== badge || score.previousElementSibling !== stars) badge.insertBefore(score, stars.nextSibling);
      };
      new MutationObserver(sync).observe(widget, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['data-average-rating', 'data-number-of-reviews']
      });
      sync();
    });
  }

  function initialize(scope = document) {
    scope.querySelectorAll('product-view.pdp-v1').forEach(setupReviewScore);
    scope.querySelectorAll('product-view.pdp-v1').forEach(setupSizePreview);
    setupDelivery(scope);
    scope.querySelectorAll('product-view.pdp-v1').forEach(setupPurchaseClarity);
    scope.querySelectorAll('[data-pdp-reviews]').forEach(setupReviews);
    scope.querySelectorAll('[data-pdp-related-loader]').forEach(setupRecommendations);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();

